import React from 'react';
import { Button, Table, Tooltip } from 'antd';
import * as R from 'ramda';
import moment from 'moment';
import { DollarOutlined, SmileTwoTone, StarTwoTone } from '@ant-design/icons';
import { IBooking, IUnit } from '../../types/models';

// Copied from BookingRoundPicksTable, assuming OwnerType is compatible with the 'owner' property here
interface IHouseOwner {
  id: number;
  fraction_numbers: number[];
  owner: any;
}

const findLongWeekend = (checkin: any, checkout: any) => {
  if (!checkin || !checkout) return false;
  const longWeekends2025 = [
    { date: '2025-01-29', holiday: 'Día de la Constitución' },
    { date: '2025-03-12', holiday: 'Natalicio de Benito Juárez' },
    { date: '2025-04-30', holiday: 'Día del Trabajo' },
    { date: '2025-09-10', holiday: 'Día de la Independencia' },
    { date: '2025-11-12', holiday: 'Día de la Revolución' },
  ];
  const start = new Date(checkin).getTime();
  const end = new Date(checkout).getTime();
  return longWeekends2025.find(holiday => {
    const holidayDate = new Date(holiday.date).getTime();
    return holidayDate >= start && holidayDate <= end;
  }) || null;
};

function rotateArrayLeft(arr: any[], n: number) {
  const rotationIndex = n % arr.length;
  const leftPart = arr.slice(rotationIndex);
  const rightPart = arr.slice(0, rotationIndex);
  return leftPart.concat(rightPart);
}

interface BookingsTableProps {
  bookings: IBooking[];
  unit: IUnit & { fractions_count?: number, owners?: IHouseOwner[], start_index?: number };
  setBookingToEdit: (booking: IBooking) => void;
}

function BookingsTable({ bookings, unit, setBookingToEdit }: BookingsTableProps) {
  const fractionsCount = unit.fractions || 0;
  const houseOwners: any[] = [...(unit.unit_ownerships || [])];
  const start_index = unit.start_index || 1;
  const numberOfRounds = bookings.length > 0 ? Math.max(...bookings.map(b => b.data?.assignment.round || 0)) : 0;

  if (!unit || fractionsCount === 0) {
    return null;
  }

  const columns = [
    { title: '', dataIndex: 'name', width: 16, fixed: 'left', render: (r: any) => <small style={{ marginLeft: 4 }}>{r}</small> },
    ...rotateArrayLeft(R.range(1, fractionsCount + 1), start_index - 1).map(fn => {
      const ho = houseOwners.find(ho => ho.fraction_numbers.includes(fn));

      return ({
        title: (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 6 }}>
            <Tooltip
              title={(
                <div>
                  <div>{ho?.owner.full_name}+</div>
                  <div>Fraction #{fn}</div>
                </div>
              )}
            >
              <div style={{ letterSpacing: -0.5, fontWeight: ho?.id ? '500' : '400', opacity: !!ho ? 1 : 0.5 }}>
                {ho?.owner.first_name?.split(" ")[0] || 'Empty'} (#{fn})
              </div>
            </Tooltip>
          </div>
        ),
        render: (rr: any) => {
          const booking = bookings.find(b => b.data?.assignment?.fraction === fn && b.data?.assignment.round === rr.round);
          if (!booking) return null;

          const rank = booking.data?.assignment?.rank;
          const longWeekend = findLongWeekend(booking.start_date, booking.end_date);

          return (
            <Tooltip
              title={(
                <ul style={{ padding: '0 0 0 12px', margin: 0 }}>
                  {booking.is_preferred && (<li>Owner's Preferred <SmileTwoTone /></li>)}
                  {longWeekend && (
                    <li>{longWeekend.date} {longWeekend.holiday} <StarTwoTone twoToneColor="orange"
                                                                            style={{ fontSize: 14 }} />
                    </li>)}
                  {rank && <li>#{rank} in Price Rank <DollarOutlined /></li>}
                  {booking.data?.assignment?.week && <li>Week {booking.data.assignment.week} of the year</li>}
                </ul>
              )}
              style={{ textAlign: 'center' }}
            >
              <div style={{ padding: '12px 6px' }} onClick={() => setBookingToEdit(booking)}>
                <div className="text-center fs-2 fw-500" style={{ letterSpacing: -0.5 }}>
                  {moment(booking.start_date).format('MMM D')}-{moment(booking.start_date).format('MMM') === moment(booking.end_date).format('MMM') ? moment(booking.end_date).format('D') : moment(booking.end_date).format('MMM D')}
                </div>
                <div className="fs-1-5" style={{
                  marginTop: 2,
                  display: 'flex',
                  gap: 12,
                  alignItems: 'center',
                  justifyContent: 'center',
                  letterSpacing: -0.5,
                }}>
                  {rank && <span><DollarOutlined style={{ fontSize: 12 }} /> #{rank}</span>}
                  {booking.is_preferred && <SmileTwoTone style={{ fontSize: 14 }} />}
                  {longWeekend && <StarTwoTone twoToneColor="orange" style={{ fontSize: 14 }} />}
                </div>
              </div>
            </Tooltip>
          );
        },
        width: 140,
      });
    }),
  ];

  return (
    <>
      <Table
        bordered={false}
        columns={columns as any}
        dataSource={R.range(1, numberOfRounds + 1).map(roundNum => ({ name: roundNum.toString(), round: roundNum }))}
        pagination={false}
        size="middle"
        style={{ width: '100%' }}
        rowKey="round"
      />
      <div style={{ display: 'flex', flexDirection: 'row', gap: 16, alignItems: 'center', padding: '6px 12px' }}>
        <div className="fs-2-66 fw-500">Legend:</div>
        <div>
          <DollarOutlined /> Price Rank
        </div>
        <div>
          <SmileTwoTone /> Preferred week
        </div>
        <div>
          <StarTwoTone twoToneColor="orange" /> Long weekend
        </div>
      </div>
    </>
  );
}

export default BookingsTable;
